import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import { useAuth } from '../../context/AuthContext';
import { BRANDING } from '../../config/branding';

const Login = () => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async () => {
    const ok = await login({ username, pin });
    if (ok) {
      navigate('/dashboard');
      return;
    }
    alert('Credenciales incorrectas. Inténtalo nuevamente.');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #180342 0%, #3f0f88 50%, #621fd1 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.12), transparent 45%), radial-gradient(circle at 80% 0%, rgba(0,255,255,0.18), transparent 25%)',
          opacity: 0.85,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 50% 60%, rgba(124,58,237,0.25), transparent 55%), radial-gradient(circle at 10% 80%, rgba(0,212,255,0.12), transparent 35%)',
          opacity: 0.9,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 5,
            bgcolor: 'rgba(8, 10, 30, 0.70)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 30px 80px rgba(11,16,58,0.75)',
            backdropFilter: 'blur(18px)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -2,
              background:
                'linear-gradient(120deg, rgba(0,212,255,0.35), rgba(124,58,237,0.35), rgba(0,212,255,0.12))',
              opacity: 0.55,
              filter: 'blur(18px)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 55%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 3,
                    display: 'grid',
                    placeItems: 'center',
                    background:
                      'linear-gradient(135deg, rgba(0,212,255,0.35), rgba(124,58,237,0.35))',
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                >
                  <LockRoundedIcon sx={{ color: 'rgba(248,250,252,0.95)' }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: 'rgba(248,250,252,0.96)',
                      lineHeight: 1,
                    }}
                  >
                    Acceso al panel
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.78)' }}>
                    {BRANDING.companyName}
                  </Typography>
                </Box>
              </Stack>
              <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  color: 'rgba(226,232,240,0.85)',
                  border: '1px solid rgba(148,163,184,0.25)',
                  bgcolor: 'rgba(15,23,42,0.25)',
                  '&:hover': { bgcolor: 'rgba(30,41,59,0.55)' },
                }}
              >
                Volver
              </Button>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)', mb: 3 }} />

            <Box
              component="form"
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              <TextField
                label="Usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                required
                autoComplete="username"
                InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.75)' } }}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'rgba(2,6,23,0.35)',
                    borderRadius: 3,
                    color: 'rgba(248,250,252,0.92)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(148,163,184,0.25)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(226,232,240,0.35)',
                  },
                }}
              />
              <TextField
                label="PIN"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                fullWidth
                required
                autoComplete="current-password"
                InputLabelProps={{ sx: { color: 'rgba(226,232,240,0.75)' } }}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'rgba(2,6,23,0.35)',
                    borderRadius: 3,
                    color: 'rgba(248,250,252,0.92)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(148,163,184,0.25)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(226,232,240,0.35)',
                  },
                }}
              />

              <Button
                variant="contained"
                type="submit"
                sx={{
                  mt: 0.5,
                  borderRadius: 999,
                  py: 1.3,
                  fontWeight: 800,
                  textTransform: 'none',
                  backgroundImage: 'linear-gradient(90deg, #00d4ff, #7c3aed)',
                  boxShadow: '0 18px 40px rgba(0,212,255,0.10)',
                }}
              >
                Entrar
              </Button>

              <Typography
                variant="caption"
                sx={{ color: 'rgba(226,232,240,0.65)', textAlign: 'center', mt: 0.5 }}
              >
                Acceso interno. El perfil de empresa del sitio se controla con{' '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  REACT_APP_COMPANY_PROFILE
                </Box>
                .
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;

